import React from 'react';
import {useSelector} from 'react-redux';
import './ShopChestItem.css';
import PageButton from '../../../elements/PageButton/PageButton';
import {getChestIconPath} from '../../../../utils/inventory/getChestIconPath';
import {getFormattedPrice} from '../../../../utils/price/getFormattedPrice';
import {calcChestsTotalPrice, CHEST_DISCOUNT_MIN_QUANTITY} from '../../../../utils/price/calcChestsTotalPrice';
import {MENU_SOUND_MANAGER} from "../../../../scenes/MenuSoundManager";
import {CHESTS} from "../../../../constants/chests";
import {LoadedImage} from "../../../EmblaCarousel/FactionsCarousel";
import {MINUS_SIGN} from "../../../../constants/text";
import {addClassNameIfTrue} from "../../../../utils/other/addClassNameIfTrue";

const PAGE_ID = 'ShopPage';

function ShopChestItem({chest, onBuy, isOwned = false, isOpened = false, onOpenInventory}) {
  // Премиум-перк: сундуки за кристаллы на 10% дешевле — на плашке сразу
  // отображаются цены со скидкой (тот же расчет, что при покупке и на сервере)
  const premiumExpiresAt = useSelector((state) => state.private.auth.premiumExpiresAt);
  const isPremiumActive = Boolean(premiumExpiresAt) && new Date(premiumExpiresAt).getTime() > Date.now();
  const isNumensPrice = !chest.priceCrystals && chest.priceNumens > 0;
  // Бейджи у кнопок показывают фактический суммарный процент скидки
  // (опт + премиум): у обычной кнопки он ненулевой только при премиуме
  const {
    totalPrice: price,
    effectiveDiscountPercents: singleDiscount,
  } = calcChestsTotalPrice(chest, 1, {isPremiumActive});

  // Оптовое предложение «x10»: только у сундуков со скидкой за 10+ штук
  const x10Discount = chest.discountPercentsFor10ItemsPurchase || 0;
  const hasX10Offer = !isOwned && !isOpened && x10Discount > 0;
  const {
    totalPrice: x10Price,
    effectiveDiscountPercents: x10EffectiveDiscount,
  } = calcChestsTotalPrice(chest, CHEST_DISCOUNT_MIN_QUANTITY, {isPremiumActive});

  const playChestSound = () => {
    if (chest.id === CHESTS.ETERNAL) {
      MENU_SOUND_MANAGER.playChestEternal();
    } else {
      MENU_SOUND_MANAGER.playChestOpen();
    }
  };

  const onButtonClick = (event) => {
    event.stopPropagation();

    if (isOpened) {
      MENU_SOUND_MANAGER.playChestEternal();
      onBuy?.(chest.id);
      return;
    }

    if (isOwned) {
      onOpenInventory?.();
      return;
    }

    playChestSound();
    onBuy?.(chest.id);
  };

  const onX10ButtonClick = (event) => {
    event.stopPropagation();
    playChestSound();
    onBuy?.(chest.id, CHEST_DISCOUNT_MIN_QUANTITY);
  };

  return (
    <div
      className={`shop-chest-item${isOwned ? ' shop-chest-item_owned' : ''}${isOpened ? ' shop-chest-item_opened' : ''}`}
      onClick={onButtonClick}
      style={{
        background: chest.backgroundColor,
      }}
    >
      <LoadedImage
        className="shop-chest-icon"
        src={getChestIconPath(chest.id)}
        alt={chest.name}
        placeholderSrc="/img/icons/chest.png"
      />
      <div
        className="shop-chest-name"
        style={{
          color: chest.textColor,
        }}
      >
        {chest.name}
      </div>

      {chest.id === CHESTS.ETERNAL && (
        <div className="shop-chest-eternal-banner">
          <span className="shop-chest-eternal-banner-icon" role="img" aria-label="Все фракции"/>
          <span className="shop-chest-eternal-banner-label">все фракции</span>
        </div>
      )}

      <PageButton
        className={`shop-chest-buy-button ${addClassNameIfTrue(hasX10Offer, 'shop-chest-buy-button-has-10x-offer')}`}
        onClick={onButtonClick}
        pageId={PAGE_ID}
        buttonId={`buy_chest_${chest.id}`}
        title={isOpened ? `${chest.name} уже открыт` : (isOwned ? `Открыть ${chest.name}` : `Купить ${chest.name}`)}
        noSound={true}
      >
        {isOpened || isOwned ? (
          <span className="shop-chest-buy-button-status">{isOpened ? 'Открыт' : 'Открыть'}</span>
        ) : (
          <>
            <span className={`shop-chest-buy-button-price ${isNumensPrice ? 'text-coins' : ''}`}>
              {getFormattedPrice(price)}
            </span>
            <img
              className="shop-chest-price-icon"
              src={isNumensPrice ? '/img/icons/numens.png' : '/img/icons/crystal.png'}
              alt={isNumensPrice ? 'Нумены' : 'Кристаллы'}
            />
            {singleDiscount > 0 && (
              <span className="shop-chest-x10-discount-badge">
                {MINUS_SIGN}{singleDiscount}%
              </span>
            )}
          </>
        )}
      </PageButton>

      {hasX10Offer && (
        <PageButton
          className="shop-chest-buy-button shop-chest-x10-button"
          onClick={onX10ButtonClick}
          pageId={PAGE_ID}
          buttonId={`buy_chest_x10_${chest.id}`}
          title={`Купить ${CHEST_DISCOUNT_MIN_QUANTITY} шт. со скидкой ${x10EffectiveDiscount}%`}
          noSound={true}
        >
          <span className="shop-chest-x10-label">10 шт.</span>
          <span className="shop-chest-x10-price-line">
            <span className={`shop-chest-buy-button-price ${isNumensPrice ? 'text-coins' : ''}`}>
              {getFormattedPrice(x10Price)}
            </span>
            <img
              className="shop-chest-price-icon"
              src={isNumensPrice ? '/img/icons/numens.png' : '/img/icons/crystal.png'}
              alt={isNumensPrice ? 'Нумены' : 'Кристаллы'}
              title={isNumensPrice ? 'Нумены' : 'Кристаллы'}
            />
          </span>
          <span
            className={`shop-chest-x10-discount-badge ${addClassNameIfTrue(chest.id === CHESTS.RANDOM_LEGENDARY, 'shop-chest-x10-discount-badge-primary')}`}
          >
            {MINUS_SIGN}{x10EffectiveDiscount}%
          </span>
        </PageButton>
      )}
    </div>
  );
}

export default ShopChestItem;
